"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePositionTPSLParams = void 0;
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const trade_utils_lib_1 = require("trade_utils_lib");
const TriggersApi_1 = require("../../../../apis/TriggersApi");
const FuturesConfig_1 = require("../../../../component/FuturesConfig");
const interface_1 = require("../../../../interface");
const CustomTriggersProvider_1 = require("../../../CustomOrders/CustomTriggersProvider");
const CalculateUtils_1 = require("../CalculateUtils");
const TPSLStore_1 = require("./TPSLStore");
const TPSLUtils_1 = require("./TPSLUtils");
const types_1 = require("./types");
const common_business_1 = require("@pionex-web-kit/common-business");
/**设置/编辑仓位的止盈止损 */
function usePositionTPSLParams(props) {
    var _a;
    const { position: originPosition, associateOrder, latestPrice, precision = 0, quoteDisplay, mode, accountType, markPrice, isOneWay, clientPairId, isPnlMode, triggerPriceType = interface_1.TriggerPriceType.LAST_PRICE, closeAll, } = props;
    const { trigger_price, trigger_price_type, trigger_id, type = interface_1.CustomOrderType.CLOSE, amount, price } = associateOrder || {};
    // 由于associateOrder是异步传入的，通过initWithOrder来控制初始化逻辑
    const initWithOrder = (0, react_1.useRef)(false);
    // 将Position信息使用ref缓存起来，方便后续修改Position中的值
    const positionRef = (0, react_1.useRef)(Object.assign({}, originPosition));
    const position = positionRef.current;
    const { position_side, maint_price = "", symbol = "", position_id, isolated_mode } = position;
    const providers = (0, TPSLUtils_1.useTpSlProviders)(position);
    const tradeQty = (0, react_1.useMemo)(() => new trade_utils_lib_1.SafeDecimal(position.positionQuantity).abs().toString(), [position.positionQuantity]);
    const _closeMode = (0, react_1.useMemo)(() => {
        if (closeAll !== undefined) {
            return closeAll ? types_1.TPSLCloseMode.CloseAll : types_1.TPSLCloseMode.CloseAmount;
        }
        return type === interface_1.CustomOrderType.CLOSE ? types_1.TPSLCloseMode.CloseAll : types_1.TPSLCloseMode.CloseAmount;
    }, [closeAll, type]);
    const store = (0, TPSLStore_1.useFuturesTPSLStore)(Object.assign({ trigger_price,
        trigger_price_type, 
        // 仓位关联的条件单限价，将作为设置限价止盈止损的初始价格
        trigger_limit_price: price, latestPrice, limitPrice: latestPrice, mode,
        precision,
        quoteDisplay,
        position_side,
        maint_price,
        markPrice, scenes: TPSLStore_1.TPSLScene.Position, isOneWay, isolateMode: !CalculateUtils_1.CalculateUtils.isOrderCrossMode(isolated_mode), closeMode: _closeMode, tradeQty, closeQty: amount, isPnlMode,
        symbol, currencyPrecision: (_a = position.currencyPrecision) !== null && _a !== void 0 ? _a : 2, leverage: position.leverage, type: associateOrder === null || associateOrder === void 0 ? void 0 : associateOrder.type }, providers));
    const { setPercent, setTriggerPrice, setPriceType, triggerPrice, percent, priceType, profitInfo, checkUserModified, changePercent, changePrice, resetParams, latestPricePercent, profit, changeProfit, validateUserInput, setEstPnl, closeQty, setCloseQty, closeMode, setCloseMode, validateCloseSetting, showSlLiquidPriceWarning, closeSettingWarning, calcPnlQty, percentText, sliderValue, changePercentText, changeSidlerValue, scenes, 
    // 当按照限价设置止盈止损时，用户最终输入的价格
    triggerLimitPrice, changeTriggerLimitPrice, checkLimit, toggleCheckLimit, changePricePercent, pricePercent, } = store;
    // 基于用户设置的平仓量更新当前仓位的持仓数量
    positionRef.current.closeQty = calcPnlQty;
    // 该hooks用于异步初始化
    (0, react_1.useEffect)(() => {
        if (initWithOrder.current || !associateOrder) {
            return;
        }
        initWithOrder.current = true;
        // 基于传入的条件单信息来更新百分比，触发价格等字段
        const percent = TPSLUtils_1.TPSLUtils.getPercent(position, associateOrder.trigger_price, mode);
        setPercent(percent);
        setTriggerPrice(associateOrder.trigger_price);
        setPriceType(associateOrder.trigger_price_type);
        const pnl = TPSLUtils_1.TPSLUtils.getEstPnl(position, associateOrder.trigger_price, mode);
        setEstPnl(TPSLUtils_1.TPSLUtils.formatPnl(pnl, (0, FuturesConfig_1.getFuturesCoinPrecision)(position.currency)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [associateOrder]);
    // 当前价格模式变跟之后，重新设置价格模式
    (0, react_1.useEffect)(() => {
        if (!trigger_price_type) {
            setPriceType(triggerPriceType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerPriceType]);
    const getCreateOrderParams = (0, common_business_1.usePersistFn)(() => {
        // 检测触发止盈止损之后是否进行部分平仓
        // const closeMode = new SafeDecimal(closeQty).gt(0) ? TPSLCloseMode.CloseAmount : TPSLCloseMode.CloseAll;
        const type = (0, TPSLUtils_1.getCreateTriggerOrderType)({ closeMode, checkLimit });
        const params = {
            accountType,
            symbol,
            // 当部分平仓时，需要设置type位CustomOrderType.MARKET_QTY，否则设置type为CustomOrderType.CLOSE
            type,
            side: position_side === interface_1.FuturePositionSide.LONG ? interface_1.CustomOrderSide.SELL : interface_1.CustomOrderSide.BUY,
            position_side,
            trigger_position_id: position_id,
            trigger_price: triggerPrice,
            trigger_price_type: priceType,
            trigger_type: TPSLUtils_1.TPSLUtils.getTriggerType(mode, position_side),
            client_position_pair_id: clientPairId,
        };
        if (closeMode === types_1.TPSLCloseMode.CloseAmount) {
            // 仅部分平仓时需要传入平仓的数量，完全平仓不需要传入数量
            params.amount = closeQty;
        }
        if (checkLimit) {
            params.price = triggerLimitPrice;
        }
        return params;
    });
    /**
     * buy reduce     short buy   平空
     *  sell  reduce      long sell    平多
     */
    const createOrder = (0, react_1.useCallback)(() => {
        const params = getCreateOrderParams();
        // 新建条件单
        return (0, TriggersApi_1.createTriggerOrder)(params);
    }, [getCreateOrderParams]);
    /**
     * 使用后端提供的接口来编辑条件单
     * https://pionex.atlassian.net/wiki/spaces/backend/pages/155844802/API#%E4%BF%AE%E6%94%B9%E6%9D%A1%E4%BB%B6%E5%8D%95
     */
    const editOrder = (0, react_1.useCallback)((trigger_id) => {
        const { accountType, symbol, type, price, amount, trigger_price, trigger_price_type } = getCreateOrderParams();
        return (0, TriggersApi_1.modifyTriggerOrder)({
            accountType,
            symbol,
            trigger_id,
            trigger_price,
            trigger_price_type,
            order_type: type.toUpperCase(),
            qty: amount,
            price,
        });
    }, [getCreateOrderParams]);
    /**
     * 校验用户的输入，返回executor给调用者 @see {TPSLValidate}
     */
    const validate = (0, react_1.useCallback)(() => {
        const error = profitInfo.error || validateCloseSetting();
        const noop = {
            type: "IDLE",
            executor() {
                return (0, rxjs_1.of)(null);
            },
        };
        if (error && error.code === TPSLUtils_1.TPSLErrorCode.INVALID_PRICE) {
            // 如果未设置价格，需要判断当前是否已经绑定了条件单，如果已绑定条件单则说明用户想取消订单
            if (trigger_id) {
                // 取消订单
                return (0, rxjs_1.of)({
                    type: "CANCEL_ORDER",
                    executor: () => (0, CustomTriggersProvider_1.cancelTriggerOrder)({
                        accountType,
                        symbol,
                        triggerId: trigger_id,
                    }),
                });
            }
            else {
                // 没有任何操作
                return (0, rxjs_1.of)(noop);
            }
        }
        // 不存在错误
        if (!error) {
            // 检测用户是否修改过参数，如果没有修改过任何参数，则啥也不做
            if (!checkUserModified()) {
                // 未发生变跟
                return (0, rxjs_1.of)(noop);
            }
            // 检测是否是编辑订单场景，如果是编辑订单则需要先取消订单，再新建订单
            if (trigger_id) {
                // 先取消订单，再重新下单
                return (0, rxjs_1.of)({
                    type: "UPDATE_ORDER",
                    executor: () => editOrder(trigger_id),
                });
            }
            // 非编辑订单场景，则直接新建订单
            return (0, rxjs_1.of)({
                type: "CREATE_ORDER",
                executor: createOrder,
            });
        }
        // 其他错误时，则直接抛出错误
        return (0, rxjs_1.throwError)(error);
    }, [accountType, checkUserModified, createOrder, editOrder, profitInfo.error, symbol, trigger_id, validateCloseSetting]);
    return {
        changePercent,
        percent,
        changePrice,
        triggerPrice,
        estPnl: profitInfo,
        validate,
        showSlLiquidPriceWarning,
        latestPrice,
        estLiqPrice: maint_price,
        priceType,
        setPriceType,
        resetParams,
        latestPricePercent,
        checkUserModified,
        profit,
        changeProfit,
        validateUserInput,
        closeQty,
        setCloseQty,
        closeMode,
        setCloseMode,
        validateCloseSetting,
        closeSettingWarning,
        calcPnlQty,
        percentText,
        sliderValue,
        changePercentText,
        changeSidlerValue,
        tradeQty,
        scenes,
        triggerLimitPrice,
        changeTriggerLimitPrice,
        checkLimit,
        toggleCheckLimit,
        pricePercent,
        changePricePercent,
    };
}
exports.usePositionTPSLParams = usePositionTPSLParams;
